/**
 * @module App
 * @version 2.284.0
 * @description Haupt-App-Komponente mit React Router, Layout-Verwaltung
 * und rollenbasiertem Routing. Unterscheidet zwischen Admin-Layout
 * (Sidebar + TopBar), Kunden-Portal und Mitarbeiter-Portal.
 * Enthaelt alle Route-Definitionen und Auth-Guards.
 *
 * @usage Einstiegspunkt der Frontend-Anwendung (main.jsx -> App)
 */
import { useState, useEffect, useRef } from 'react';
import { Routes, Route, useLocation, Navigate, useNavigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { useTheme } from './context/ThemeContext';
import { useLanguage } from './context/LanguageContext';
import { AppModeProvider } from './context/AppModeContext';
import { istKioskSession } from './utils/workSession';
import { geraetHeaders, getGeraetToken } from './utils/kioskDevice';
import Sidebar from './components/Sidebar';
import PortalSidebar from './components/PortalSidebar';
import TopBar from './components/TopBar';
import ErrorBoundary from './components/ErrorBoundary';
import HomePage from './pages/HomePage';
import Home from './pages/Home';
import Workforce from './pages/Workforce';
import MitarbeiterPage from './pages/MitarbeiterPage';
import MitarbeiterDetail from './pages/MitarbeiterDetail';
import KundenPage from './pages/KundenPage';
import Contacts from './pages/Contacts';
import StandortePage from './pages/StandortePage';
import Forecast from './pages/Forecast';
import ForecastQuality from './pages/ForecastQuality';
import ForecastBulk from './pages/ForecastBulk';
import Commitments from './pages/Commitments';
import CommitmentDetail from './pages/CommitmentDetail';
import CommitmentsZurGenehmigung from './pages/CommitmentsZurGenehmigung';
import CompanyRegistrations from './pages/CompanyRegistrations';
import ForecastOverview from './pages/ForecastOverview';
import Schichtplanung from './pages/Schichtplanung';
import WochenplanPage from './pages/WochenplanPage';
import Personalplanung from './pages/Personalplanung';
import RotationTV from './pages/RotationTV';
import Operations from './pages/Operations';
import OutboundInvestigations from './pages/OutboundInvestigations';
import Feiertage from './pages/Feiertage';
import KundenDetail from './pages/KundenDetail';
import Performance from './pages/Performance';
import Rohdaten from './pages/Rohdaten';
import ApiConfig from './pages/ApiConfig';
import Segmentanalyse from './pages/Segmentanalyse';
import Anwesenheiten from './pages/Anwesenheiten';
import AusnahmenPage from './pages/Ausnahmen';
import Login from './pages/Login';
import Invite from './pages/Invite';
import Profile from './pages/Profile';
import UserAdmin from './pages/UserAdmin';
import Notifications from './pages/Notifications';
import BugReportVerify from './pages/BugReportVerify';
import BugReportInbox from './pages/BugReportInbox';
import BugReportDetail from './pages/BugReportDetail';
import Cases from './pages/Cases';
import CasesKonfiguration from './pages/CasesKonfiguration';
import CasesEinstellungen from './pages/CasesEinstellungen';
import CasesReports from './pages/CasesReports';
import CasesFlowaiReport from './pages/CasesFlowaiReport';
import CarrierKontrolle from './pages/CarrierKontrolle';
import Deckungsbeitrag from './pages/Deckungsbeitrag';
import InboundManagement from './pages/InboundManagement';
import Warehouse from './pages/Warehouse';
import WarehouseConfig from './pages/WarehouseConfig';
import WarehouseLagerplaetze from './pages/WarehouseLagerplaetze';
import WarehouseSimulation from './pages/WarehouseSimulation';
import PortalDashboard, { ForecastBanner } from './pages/PortalDashboard';
import PortalForecasts from './pages/PortalForecasts';
import PortalRechnungen from './pages/PortalRechnungen';
import PortalStats from './pages/PortalStats';
import PortalAddressClarifications from './pages/PortalAddressClarifications';
import PortalClaims from './pages/portal/PortalClaims';
import PortalVereinbarungen from './pages/PortalVereinbarungen';
import PortalVereinbarungDetail from './pages/PortalVereinbarungDetail';
import MaPortal from './pages/MaPortal';
import MaPortalNews from './pages/MaPortalNews';
import MaPortalProfil from './pages/MaPortalProfil';
import MaPortalDokumente from './pages/MaPortalDokumente';
import MaPortalZeiten from './pages/MaPortalZeiten';
import PortalAbwesenheiten from './pages/PortalAbwesenheiten';
import PortalZeitkontoPage from './pages/PortalZeitkontoPage';
import PortalVertraege from './pages/PortalVertraege';
import Handbuch from './pages/Handbuch';
import DevHandbuch from './pages/DevHandbuch';
import Changelog from './pages/Changelog';
import DeploymentPage from './pages/DeploymentPage';
import SystemPulse from './pages/SystemPulse';
import Einstellungen from './pages/Einstellungen';
import NotificationEventsAdmin from './pages/NotificationEventsAdmin';
import RolePermissionsAdmin from './pages/admin/RolePermissionsAdmin';
import ScenariosAdmin from './pages/admin/ScenariosAdmin';
import NotificationLogAdmin from './pages/admin/NotificationLogAdmin';
import ChatAdmin from './pages/admin/ChatAdmin';
import IntegrationenAdmin from './pages/admin/IntegrationenAdmin';
import OAuthConsent from './pages/OAuthConsent';
import FreshdeskAnalysePage from './pages/FreshdeskAnalysePage';
import SegmentConfig from './pages/SegmentConfig';
import Reports from './pages/Reports';
import IdleTime from './pages/IdleTime';
import ZeitvorgabenAnalyse from './pages/ZeitvorgabenAnalyse';
import Aufgaben from './pages/Aufgaben';
import MeineAufgaben from './pages/MeineAufgaben';
import Kalender from './pages/Kalender';
import TerminBuchen from './pages/TerminBuchen';
import EmailVerwaltung from './pages/EmailVerwaltung';
import SmsTemplates from './pages/SmsTemplates';
import SurveyPublic from './pages/SurveyPublic';
import Zufriedenheit from './pages/Zufriedenheit';
import CaseActionPublic from './pages/CaseActionPublic';
import AdressklaerungPage from './pages/AdressklaerungPage';
import AdressklaerungStats from './pages/AdressklaerungStats';
import SalesDashboard from './pages/sales/SalesDashboard';
import SalesFunnels from './pages/sales/SalesFunnels';
import SalesAnfragen from './pages/sales/SalesAnfragen';
import SalesEinstellungen from './pages/sales/SalesEinstellungen';
import WorkflowEditor from './pages/sales/WorkflowEditor';
import SalesWorkflows from './pages/sales/SalesWorkflows';
import SalesOnboarding from './pages/sales/SalesOnboarding';
// MeineOnboardingAufgaben removed — Sales onboarding tasks → /meine-aufgaben (user_tasks)
import SalesChat from './pages/sales/SalesChat';
import LeadFinder from './pages/sales/LeadFinder';
import LeadDetail from './pages/sales/LeadDetail';
import ClaimsDashboard from './pages/claims/ClaimsDashboard';
import ClaimDetail from './pages/claims/ClaimDetail';
import ClaimsFormular from './pages/claims/ClaimsFormular';
import ClaimsKnowledge from './pages/claims/ClaimsKnowledge';
import ClaimsSettings from './pages/claims/ClaimsSettings';
import ClaimsFormularPublic from './pages/claims/ClaimsFormularPublic';
import UnassignedInbox from './pages/claims/UnassignedInbox';
import FlowAICockpit from './pages/flowai/FlowAICockpit';
import FlowAIUebersicht from './pages/flowai/FlowAIUebersicht';
import FlowAIWorkspaceList from './pages/flowai/FlowAIWorkspaceList';
import FlowAIWorkspaceDetail from './pages/flowai/FlowAIWorkspaceDetail';
import FlowAIPlaybookList from './pages/flowai/FlowAIPlaybookList';
import FlowAIPlaybookEditor from './pages/flowai/FlowAIPlaybookEditor';
import FlowAIPlaybookBuilder from './pages/flowai/FlowAIPlaybookBuilder';
import FlowAISkillList from './pages/flowai/FlowAISkillList';
import FlowAISkillEditor from './pages/flowai/FlowAISkillEditor';
import FlowAIApprovalInbox from './pages/flowai/FlowAIApprovalInbox';
import FlowAIAudit from './pages/flowai/FlowAIAudit';
import FlowAIMemory from './pages/flowai/FlowAIMemory';
import FlowAIEvents from './pages/flowai/FlowAIEvents';
import InvestorApp from './pages/investor/InvestorApp';
import FlowAIEmailConfig from './pages/flowai/FlowAIEmailConfig';
import FlowAISafety from './pages/flowai/FlowAISafety';
import FlowAIChat from './pages/flowai/FlowAIChat';
import FlowAIVoice from './pages/flowai/FlowAIVoice';
import SoftphoneTest from './pages/SoftphoneTest';
import PreiskalkulatorPage from './pages/PreiskalkulatorPage';
import PreiskalkulatorConfig from './pages/PreiskalkulatorConfig';
import PreiskalkulatorRouten from './pages/PreiskalkulatorRouten';
import PreiskalkulatorAnfragen from './pages/PreiskalkulatorAnfragen';
import PreiskalkulatorPublic from './pages/PreiskalkulatorPublic';
import PreiskalkulatorPDF from './pages/PreiskalkulatorPDF';
import ProzesseAbteilungenPage from './pages/ProzesseAbteilungenPage';
import ProzessAbteilungDetailPage from './pages/ProzessAbteilungDetailPage';
import ProzessDetailPage from './pages/ProzessDetailPage';
import ProzessTelegramPage from './pages/ProzessTelegramPage';
import FlexLandingPage from './pages/FlexLandingPage';
import FlexBewerberPage from './pages/FlexBewerberPage';
import FlexBewerberDetail from './pages/FlexBewerberDetail';
import FlexEinsaetzePage from './pages/FlexEinsaetzePage';
import FlexEinsatzDetail from './pages/FlexEinsatzDetail';
import FlexZusagePage from './pages/FlexZusagePage';
import ProzessMobilePage from './pages/ProzessMobilePage';
import QmTestMagicPage from './pages/QmTestMagicPage';
import QmFeedbackPage from './pages/QmFeedbackPage';
import QmPipeline from './pages/QmPipeline';
import FlexPoolBestaetigung from './pages/FlexPoolBestaetigung';
import FlexOnboarding from './pages/FlexOnboarding';
import FlexEinstellungen from './pages/FlexEinstellungen';
import FlexDashboard from './pages/FlexDashboard';
import AusweisePage from './pages/AusweisePage';
import FlexReports from './pages/FlexReports';
import FlexPoolPage from './pages/FlexPoolPage';
import RecruitingDashboardUnified from './pages/RecruitingDashboardUnified';
import RecruitingBewerberUnified from './pages/RecruitingBewerberUnified';
import RecruitingDetailUnified from './pages/RecruitingDetailUnified';
import RecruitingStellen from './pages/RecruitingStellen';
import RecruitingReportsUnified from './pages/RecruitingReportsUnified';
import RecruitingEinstellungenUnified from './pages/RecruitingEinstellungenUnified';
import AufnahmenPage from './pages/AufnahmenPage';
import TelefonieDashboardPage, { TelefonieVerlaufPage, TelefonieAufnahmenPage, TelefonieRegelnPage, TelefonieEinstellungenPage } from './pages/TelefoniePage';
import FinecomPage from './pages/FinecomPage';
import JvDeckungsbeitragPage from './pages/JvDeckungsbeitragPage';
import MitarbeiterPulsePage from './pages/MitarbeiterPulsePage';
import MitarbeiterPulseDetail from './pages/MitarbeiterPulseDetail';
import RecordingDisplayPage from './pages/RecordingDisplayPage';
import PersonalkostenPage from './pages/PersonalkostenPage';
import ControllingKpiPage from './pages/ControllingKpiPage';
import ControllingZeitKosten from './pages/ControllingZeitKosten';
import KioskPage from './pages/KioskPage';
import FlowKiosk from './pages/flowai/FlowKiosk';
import PackplatzTimer from './pages/PackplatzTimer';
import PackStationDisplay from './pages/PackStationDisplay';
import LagerTVPage from './pages/LagerTVPage';
import AbwesenheitDisplayPage from './pages/AbwesenheitDisplayPage';
import UrlaubKalenderPage from './pages/UrlaubKalenderPage';
import UrlaubAntraegePage from './pages/UrlaubAntraegePage';
import UrlaubUebersichtPage from './pages/UrlaubUebersichtPage';
import AbwesenheitenListePage from './pages/AbwesenheitenListePage';
import ZeitkontoPage from './pages/ZeitkontoPage';
import Zeiterfassung from './pages/Zeiterfassung';
import LohnverrechnungPage from './pages/LohnverrechnungPage';
import KarrierePage from './pages/KarrierePage';
import RecruitingDetail from './pages/RecruitingDetail';
import OrganigrammPage from './pages/OrganigrammPage';
import HrAudit from './pages/HrAudit';
import HrAustritte from './pages/HrAustritte';
import OnboardingPage from './pages/OnboardingPage';
import AdminHubPage from './pages/AdminHubPage';
import VertraegePage from './pages/VertraegePage';
import VertragUnterschreibenPage from './pages/VertragUnterschreibenPage';
import StammdatenPage from './pages/StammdatenPage';
import BewerberStatusPage from './pages/BewerberStatusPage';
import PinLogin from './pages/PinLogin';
import WorkLogin from './pages/WorkLogin';
import KioskGeraetSetup from './pages/KioskGeraetSetup';
import WorkBestaetigung from './pages/WorkBestaetigung';
import PortalMobileLayout from './layouts/PortalMobileLayout';
import MobileHome from './pages/mobile/MobileHome';
import MobileAbwesend from './pages/mobile/MobileAbwesend';
import MobileDokumente from './pages/mobile/MobileDokumente';
import MobileProfil from './pages/mobile/MobileProfil';
import MobileNotifications from './pages/mobile/MobileNotifications';
import PortalSchulungen from './pages/PortalSchulungen';
import PortalSchulungDetail from './pages/PortalSchulungDetail';
import PortalVorschlaege from './pages/PortalVorschlaege';
import ForecastReview from './pages/ForecastReview';
import CustomerForecastDetail from './pages/CustomerForecastDetail';
// CopilotChat (#1002) — global deaktiviert: Backend /api/copilot bleibt für potentielle Re-Aktivierung
// import CopilotChat from './components/CopilotChat';
import TimerFloatingPanel from './components/TimerFloatingPanel';

const API = import.meta.env.VITE_API_URL || '';

// Route → permission modul (matches role_permissions.modul)
const ROUTE_TO_MODUL = {
  '/operations': 'operations',
  '/operations/lieferauftraege-pruefung': 'operations',
  '/forecast': 'forecast',
  '/forecast/quality': 'forecast',
  '/forecast/bulk': 'forecast',
  '/commitments': 'commitments',
  '/commitments/zur-genehmigung': 'commitments',
  '/commitments/new': 'commitments',
  '/firmen-registrierungen': 'kunden',
  '/forecast/aktionszeitraeume': 'forecast',
  '/forecast-overview': 'forecast',
  '/forecast-review': 'customer_forecasts',
  '/schichtplanung': 'schichtplanung',
  '/schichtplanung/wochenplan': 'schichtplanung',
  '/personalplanung': 'schichtplanung',
  '/kunden-rotation/tv': 'operations',
  '/mitarbeiter': 'mitarbeiter',
  '/mitarbeiter/onboarding': 'mitarbeiter',
  '/performance': 'performance',
  '/mitarbeiter/pulse': 'performance',
  '/anwesenheiten': 'mitarbeiter,anwesenheiten_readonly',
  '/anwesenheiten/ausnahmen': 'mitarbeiter',
  '/kunden': 'kunden',
  '/cases': 'cases',
  '/standorte': 'admin',
  '/kostenstellen': 'kunden',
  '/feiertage': 'admin',
  '/rohdaten': 'admin',
  '/data-hub': 'admin',
  '/admin': 'admin',
  '/admin/users': 'benutzerverwaltung',
  '/admin/segmente': 'admin',
  '/admin/email': 'admin',
  '/admin/sms-templates': 'admin',
  '/admin/rollen': 'admin',
  '/admin/szenarien': 'admin',
  '/admin/notification-log': 'admin',
  '/admin/chat': 'admin',
  '/admin/integrationen': 'admin',
  '/carrier-kontrolle': 'carrier_kontrolle',
  '/zufriedenheit': 'cases',
  '/deckungsbeitrag': 'deckungsbeitrag',
  '/zeitvorgaben': 'deckungsbeitrag',
  '/zeitvorgaben-analyse': 'deckungsbeitrag',
  '/reports': 'reports',
  '/idle-time': 'reports',
  '/sales/dashboard': 'sales',
  '/sales/funnels': 'sales',
  '/sales/anfragen': 'sales',
  '/sales/onboarding': 'sales',
  // '/sales/onboarding/meine-aufgaben' → redirects to /meine-aufgaben
  '/sales/workflows': 'sales',
  '/sales/lead-finder': 'sales',
  '/sales/leads': 'sales',
  '/kunden-forecasts': 'customer_forecasts',
  '/sales/einstellungen': 'sales',
  '/claims': 'claims',
  '/claims/knowledge': 'claims',
  '/claims/inbox': 'claims',
  '/claims/settings': 'claims',
  '/flowai': 'flowai',
  '/flowai/uebersicht': 'flowai',
  '/flowai/workspaces': 'flowai',
  '/flowai/playbooks': 'flowai',
  '/flowai/skills': 'flowai',
  '/flowai/approvals': 'flowai',
  '/flowai/audit': 'flowai',
  '/flowai/memory': 'flowai',
  '/flowai/events': 'flowai',
  '/flowai/chat': 'flowai',
  '/flowai/voice': 'flowai',
  '/softphone-test': 'admin',
  '/chat': 'flowai',
  '/preiskalkulator': 'sales',
  '/preiskalkulator/config': 'sales',
  '/preiskalkulator/routen': 'sales',
  '/preiskalkulator/anfragen': 'sales',
  '/jv-deckungsbeitrag': 'deckungsbeitrag',
  '/controlling/kpi': 'deckungsbeitrag',
  '/controlling/zeit-kosten': 'controlling',
  '/personalkosten': 'deckungsbeitrag',
  '/finecom': 'deckungsbeitrag,finecom_partner',
  '/aufnahmen': 'admin',
  '/telefonie': 'telefonie',
  '/telefonie/verlauf': 'telefonie',
  '/telefonie/aufnahmen': 'telefonie',
  '/telefonie/regeln': 'telefonie',
  '/telefonie/einstellungen': 'telefonie',
  '/prozesse': 'admin',
  '/prozesse/abteilungen': 'admin',
  '/prozesse/telegram': 'admin',
  '/qm/feedback': 'admin',
  '/qm/pipeline': 'admin',
  '/recruiting': 'mitarbeiter',
  '/recruiting/bewerber': 'mitarbeiter',
  '/recruiting/stellen': 'mitarbeiter',
  '/recruiting/einsaetze': 'mitarbeiter',
  '/recruiting/pool': 'mitarbeiter',
  '/recruiting/reports': 'mitarbeiter',
  '/recruiting/karriere': 'mitarbeiter',
  '/recruiting/einstellungen': 'mitarbeiter',
  '/vertraege': 'mitarbeiter',
  '/flex': 'mitarbeiter',
  '/flex/bewerber': 'mitarbeiter',
  '/flex/einsaetze': 'mitarbeiter',
  '/flex/einstellungen': 'mitarbeiter',
  '/flex/pool': 'mitarbeiter',
  '/flex/reports': 'mitarbeiter',
  '/ausweise': 'mitarbeiter',
  '/organigramm': 'mitarbeiter',
  '/abwesenheiten/kalender': 'urlaub',
  '/abwesenheiten/antraege': 'urlaub',
  '/abwesenheiten/uebersicht': 'urlaub',
  '/abwesenheiten/liste': 'urlaub',
  '/zeitkonto': 'zeitkonto',
  '/zeiterfassung': 'zeiterfassung',
  '/lohnverrechnung': 'mitarbeiter',
  '/lohnverrechnung/import': 'mitarbeiter',
  '/lohnverrechnung/monats-bestaetigung': 'mitarbeiter',
  '/lohnverrechnung/lv-report': 'mitarbeiter',
  '/lohnverrechnung/einstellungen': 'mitarbeiter',
  '/einstellungen': 'einstellungen',
  '/inbound-management': 'operations',
  '/warehouse': 'operations',
  '/warehouse/config': 'operations',
  '/warehouse/lagerplaetze': 'operations',
  '/warehouse/simulation': 'operations',
  '/warehouse/einrichten': 'operations',
  '/admin/deployment': 'deployment',
  '/dev-handbuch': 'admin',
  '/handbuch': 'admin',
  '/changelog': 'admin',
  '/entwicklung/system-pulse': 'admin',
};

// Kunden-Portal Layout (für rolle='kunde')
function KundenPortalLayout({ collapsed, setCollapsed }) {
  const [kundeName, setKundeName] = useState('');
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetch(`${API}/api/portal/dashboard`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (d.kunde_name) setKundeName(d.kunde_name); })
      .catch(() => {});
  }, []);

  const exitImpersonate = async () => {
    try {
      const res = await fetch(`${API}/api/admin/exit-impersonate`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (res.ok) {
        await checkAuth();
        navigate(data.redirect || '/admin/users');
      }
    } catch { /* ignore */ }
  };

  return (
    <div className="flex h-screen bg-surface text-content overflow-hidden">
      <PortalSidebar collapsed={collapsed} setCollapsed={setCollapsed} kundeName={kundeName} />
      <div className="flex-1 flex flex-col overflow-hidden">
        {user?.impersonated_by && (
          <div className="flex items-center justify-between px-4 py-2 bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-200 flex-shrink-0">
            <div className="flex items-center gap-2 text-xs font-medium">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              Du bist als <span className="font-semibold">{kundeName || 'Kunde'}</span> eingeloggt (Admin-Modus)
            </div>
            <button onClick={exitImpersonate}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-[11px] font-medium bg-amber-200 dark:bg-amber-800 hover:bg-amber-300 dark:hover:bg-amber-700 text-amber-900 dark:text-amber-100 transition-colors">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
              Zurück zum Admin
            </button>
          </div>
        )}
        <main className="flex-1 overflow-y-auto bg-surface">
          <div className="px-6 pt-4"><ForecastBanner /></div>
          <ErrorBoundary resetKeys={[location.pathname]} label="kunden-portal">
            <Routes>
              <Route path="/portal" element={<PortalDashboard />} />
              <Route path="/portal/forecasts" element={<PortalForecasts />} />
              <Route path="/portal/rechnungen" element={<PortalRechnungen />} />
              <Route path="/portal/statistics" element={<PortalStats />} />
              <Route path="/portal/address-clarifications" element={<PortalAddressClarifications />} />
              <Route path="/portal/claims" element={<PortalClaims />} />
              <Route path="/portal/claims/new" element={<ClaimsFormular />} />
              <Route path="/portal/vereinbarungen" element={<PortalVereinbarungen />} />
              <Route path="/portal/vereinbarungen/:id" element={<PortalVereinbarungDetail />} />
              <Route path="/profil" element={<Profile />} />
              <Route path="*" element={<Navigate to="/portal" replace />} />
            </Routes>
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}

// Redirect helpers for parameterized legacy flex routes
function FlexBewerberRedirect() { const { id } = useParams(); return <Navigate to={`/recruiting/bewerber/${id}?typ=flex`} replace />; }
function FlexEinsatzRedirect() { const { id } = useParams(); return <Navigate to={`/recruiting/einsaetze/${id}`} replace />; }
// #1590: Portal-IA — Karte & Navi sollen auf dieselbe Primär-Komponente MobileAbwesend führen; ?typ= bleibt erhalten.
function PortalAbwesendRedirect() { const { search } = useLocation(); return <Navigate to={`/portal/abwesend${search || ''}`} replace />; }

function AppContent() {
  const [sidebarMode, setSidebarMode] = useState(() => {
    try {
      const stored = localStorage.getItem('weship_sidebar_mode');
      if (stored === 'pinned' || stored === 'collapsed') return stored;
      // Migrate from old boolean format
      if (localStorage.getItem('weship-sidebar-collapsed') === 'true') return 'collapsed';
      return 'pinned';
    } catch { return 'pinned'; }
  });
  const [isDev, setIsDev] = useState(false);
  useEffect(() => {
    // Only persist pinned or collapsed (hover is transient)
    localStorage.setItem('weship_sidebar_mode', sidebarMode === 'hover' ? 'collapsed' : sidebarMode);
  }, [sidebarMode]);
  useEffect(() => {
    fetch('/api/health').then(r => r.json()).then(d => {
      if (d.env === 'development') setIsDev(true);
    }).catch(() => {});
  }, []);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { loadFromBackend, setTheme, theme } = useTheme();
  const { reloadStageMapping, setLanguage, language, languageIsExplicit } = useLanguage();

  // Work mode: force German + light mode for ALL users (v2.368.1)
  const isWorkHostGlobal = typeof window !== 'undefined' && window.location.hostname === 'work.weship.eu';
  // Work-Oberflaeche = Host/Pfad/PIN-Session. Traegt die SPRACHE (#1786, unveraendert).
  const isWorkSurfaceGlobal = isWorkHostGlobal
    || (typeof window !== 'undefined' && sessionStorage.getItem('weship_work_mode') === 'true')
    || location.pathname === '/work'
    || (user?.work_mode === true);
  const isWorkModeGlobal = isWorkSurfaceGlobal || (user?.rolle === 'portal_mitarbeiter');

  // #1809-Fix: Kiosk ist eine Eigenschaft des GERAETS, nicht des Hosts und nicht
  // des Login-Wegs. work.weship.eu ist zwei Kontexte — das registrierte Terminal
  // und die MA-App am privaten Handy. Nur das registrierte Terminal ist ein
  // Shared Device und bleibt hart hell.
  const isKioskSessionGlobal = istKioskSession(user);

  // Theme aus users.theme laden — im Kiosk uebersprungen (dort gilt immer hell)
  useEffect(() => {
    if (user && !isKioskSessionGlobal) {
      loadFromBackend();
    }
  }, [user, loadFromBackend, isKioskSessionGlobal]);

  // Sprache + Theme. #1809-Fix revidiert das hostbasierte Erzwingen von Deutsch
  // aus #852 BEWUSST (Manuel-Entscheidung, keine Regression): am geteilten
  // Terminal bleibt Deutsch Pflicht, in der persoenlichen Sitzung ist es nur
  // noch Voreinstellung. Ohne diesen Unterschied waere der DE/EN-Schalter in
  // MobileProfil tote Bedienung — dasselbe Muster, das #1809 beim Theme loeste.
  // Die Voreinstellung ist noetig, weil DEFAULT_LANGUAGE 'en' ist: ein blosses
  // Weglassen des Zwangs haette Lager-Mitarbeitende still auf Englisch gesetzt.
  useEffect(() => {
    if (isKioskSessionGlobal) {
      if (language !== 'de') setLanguage('de');
    } else if (isWorkModeGlobal && !languageIsExplicit) {
      setLanguage('de');
    }
    if (isKioskSessionGlobal && theme !== 'light') setTheme('light');
  }, [isWorkModeGlobal, isKioskSessionGlobal, languageIsExplicit, language, theme, setLanguage, setTheme]);

  // #1816 AK3 — ein registriertes Terminal findet nach einem Neustart von allein
  // zurueck auf die Scan-Seite. Nach einem Stromausfall soll niemand zu drei
  // Tablets laufen muessen.
  //
  // Bewusst NUR von der Startseite und von /work aus: ein Redirect auf jedem
  // Pfad wuerde /geraet-einrichten unerreichbar machen (kein Weg mehr aus der
  // Registrierung heraus) und die bestehende Portal-Ansicht des PIN-Terminals
  // kapern. Und bewusst SERVERGEPRUEFT statt nur anhand des Tokens: der Token
  // beweist nur, dass das Geraet einmal gekoppelt war — ein widerrufenes Tablet
  // wuerde sonst dauerhaft auf eine Seite umgeleitet, die es nicht mehr nutzen
  // darf. FAIL-DIRECTION wie #1809: im Zweifel NICHT umleiten.
  const kioskRedirectGeprueft = useRef(false);
  useEffect(() => {
    if (kioskRedirectGeprueft.current) return;
    const startseite = location.pathname === '/' || location.pathname === '/work';
    if (!startseite || !getGeraetToken()) return;
    kioskRedirectGeprueft.current = true;
    fetch('/api/kiosk-geraete/status', { headers: geraetHeaders() })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d?.kiosk) navigate('/kiosk', { replace: true }); })
      .catch(() => {});
  }, [location.pathname, navigate]);

  // Load stage mapping after auth — initial load in LanguageProvider fires before cookie is set
  useEffect(() => {
    if (user) {
      reloadStageMapping();
    }
  }, [user, reloadStageMapping]);

  // Redirect-Guard: Enforce correct URL based on role
  useEffect(() => {
    if (loading || !user) return;
    if (['kunde', 'portal_kunde'].includes(user.rolle) && !location.pathname.startsWith('/portal') && location.pathname !== '/profil') {
      navigate('/portal', { replace: true });
    }
    if (user.rolle === 'portal_mitarbeiter' && !location.pathname.startsWith('/portal')) {
      navigate('/portal', { replace: true });
    }
    // jv_partner: only /finecom allowed
    if (user.rolle === 'jv_partner' && location.pathname !== '/finecom' && location.pathname !== '/profil') {
      navigate('/finecom', { replace: true });
    }
  }, [user, loading, location.pathname, navigate]);

  // Permission-based route guard
  useEffect(() => {
    if (loading || !user) return;
    if (!user.permissions) return;
    // Portal routes are always allowed
    if (location.pathname.startsWith('/portal')) return;
    // Home + Dashboard always allowed
    if (location.pathname === '/' || location.pathname === '/dashboard') return;
    const pathForCheck = location.pathname.startsWith('/kunden/') ? '/kunden'
      : location.pathname.startsWith('/cases/') ? '/cases'
      : location.pathname.startsWith('/sales/') ? '/sales/' + location.pathname.split('/')[2]
      : /^\/claims\/\d+/.test(location.pathname) ? '/claims'
      : /^\/recruiting\/bewerber\/\d+/.test(location.pathname) ? '/recruiting/bewerber'
      : /^\/flex\/bewerber\/\d+/.test(location.pathname) ? '/flex/bewerber'
      : /^\/flex\/einsaetze\/\d+/.test(location.pathname) ? '/flex/einsaetze'
      : /^\/prozesse\/\d+/.test(location.pathname) ? '/prozesse'
      : location.pathname.startsWith('/prozesse/abteilungen/') ? '/prozesse/abteilungen'
      : location.pathname;
    const modul = ROUTE_TO_MODUL[pathForCheck];
    if (modul) {
      // Support comma-separated moduls (e.g. 'deckungsbeitrag,finecom_partner')
      const moduls = modul.split(',');
      const hasAny = moduls.some(m => { const p = user.permissions[m]; return p && p !== 'none'; });
      if (!hasAny) {
        navigate(user.rolle === 'jv_partner' ? '/finecom' : '/', { replace: true });
      }
    }
  }, [user, loading, location.pathname, navigate]);

  // Public routes (no auth required) — BEFORE loading check so they never depend on auth

  const isInvite = location.pathname.startsWith('/invite/');
  const isFeedback = location.pathname.startsWith('/feedback/');
  const isCaseAction = location.pathname.startsWith('/case-action/');
  const isSalesChat = location.pathname.startsWith('/sales/chat/');
  const isClaimsPublic = location.pathname === '/claims/submit';
  const isCalculatorPublic = location.pathname.startsWith('/calculator');
  const isFlexJobs = location.pathname === '/jobs';
  const isFlexZusage = location.pathname.startsWith('/flex/zusage/');
  const isProzessMobile = location.pathname.startsWith('/p/');
  const isFlexPoolBestaetigung = location.pathname.startsWith('/flex/pool-bestaetigung/');
  const isFlexOnboarding = location.pathname.startsWith('/flex/onboarding/');
  const isRecordingDisplay = location.pathname.startsWith('/aufnahmen/display/');
  const isKiosk = location.pathname === '/kiosk';
  const isFlowKiosk = location.pathname === '/flow'; // #1391 iPad-Kiosk-Hülle (PIN-Gate, kein App-Login)
  const isPackStation = location.pathname.startsWith('/packstation');
  const isPackstationHost = window.location.hostname.includes('packplatz');
  const isLagerTV = location.pathname === '/lager-tv';
  const isAbwesenheitDisplay = location.pathname === '/abwesenheit-display';
  const isKarriere = location.pathname === '/karriere';
  const isVertragUnterschrift = location.pathname.startsWith('/vertrag/unterschreiben/');
  const isBewerberStatus = location.pathname.startsWith('/bewerber/status/');
  const isStammdaten = location.pathname.startsWith('/stammdaten/');
  const isPinLogin = location.pathname === '/pin-login';
  const isWorkLogin = location.pathname === '/work';
  const isWorkBestaetigung = location.pathname.startsWith('/work/bestaetigung/');
  const isGeraetSetup = location.pathname === '/geraet-einrichten'; // #1809-Fix Terminal-Kopplung (Code-Auth)
  const isInvestor = location.pathname.startsWith('/investor');
  const isOAuthConsent = location.pathname === '/oauth/consent'; // #1643 MCP-OAuth Consent (auth-gated, standalone)
  // Packplatz subdomain: render only PackStationDisplay
  if (isPackstationHost) {
    return (
      <ErrorBoundary label="packstation">
        <Routes>
          <Route path="*" element={<PackStationDisplay />} />
        </Routes>
      </ErrorBoundary>
    );
  }

  if (isInvestor) {
    return (
      <ErrorBoundary resetKeys={[location.pathname]} label="investor">
        <InvestorApp />
      </ErrorBoundary>
    );
  }

  if (isInvite || isFeedback || isCaseAction || isSalesChat || isClaimsPublic || isCalculatorPublic || isFlexJobs || isFlexZusage || isProzessMobile || isFlexPoolBestaetigung || isFlexOnboarding || isRecordingDisplay || isKiosk || isFlowKiosk || isPackStation || isLagerTV || isAbwesenheitDisplay || isKarriere || isVertragUnterschrift || isBewerberStatus || isStammdaten || isPinLogin || isWorkLogin || isWorkBestaetigung || isGeraetSetup) {
    return (
      <ErrorBoundary resetKeys={[location.pathname]} label="public">
      <Routes>
        <Route path="/invite/:token" element={<Invite />} />
        <Route path="/feedback/:token" element={<SurveyPublic />} />
        <Route path="/case-action/:token" element={<CaseActionPublic />} />
        <Route path="/sales/chat/:token" element={<SalesChat />} />
        <Route path="/claims/submit" element={<ClaimsFormularPublic />} />
        <Route path="/calculator" element={<PreiskalkulatorPublic />} />
        <Route path="/calculator/internal" element={<PreiskalkulatorPublic internal={true} />} />
        <Route path="/calculator/pdf" element={<PreiskalkulatorPDF />} />
        <Route path="/jobs" element={<FlexLandingPage />} />
        <Route path="/flex/zusage/:token" element={<FlexZusagePage />} />
        <Route path="/p/:token" element={<ProzessMobilePage />} />
        <Route path="/qm/test/:token" element={<QmTestMagicPage />} />
        <Route path="/flex/pool-bestaetigung/:token" element={<FlexPoolBestaetigung />} />
        <Route path="/flex/onboarding/:token" element={<FlexOnboarding />} />
        <Route path="/aufnahmen/display/:cameraId" element={<RecordingDisplayPage />} />
        <Route path="/kiosk" element={<KioskPage />} />
        <Route path="/flow" element={<FlowKiosk />} />
        <Route path="/packstation" element={<PackStationDisplay />} />
        <Route path="/packstation/:stationId" element={<PackStationDisplay />} />
        <Route path="/lager-tv" element={<LagerTVPage />} />
        <Route path="/abwesenheit-display" element={<AbwesenheitDisplayPage />} />
        <Route path="/karriere" element={<KarrierePage />} />
        <Route path="/vertrag/unterschreiben/:token" element={<VertragUnterschreibenPage />} />
        <Route path="/stammdaten/:token" element={<StammdatenPage />} />
        <Route path="/bewerber/status/:token" element={<BewerberStatusPage />} />
        <Route path="/pin-login" element={<Navigate to="/work" replace />} />
        <Route path="/work" element={<WorkLogin />} />
        <Route path="/work/bestaetigung/:token" element={<WorkBestaetigung />} />
        <Route path="/geraet-einrichten" element={<KioskGeraetSetup />} />
      </Routes>
      </ErrorBoundary>
    );
  }

  // Loading spinner — only for authenticated routes (public routes handled above)
  if (loading) {
    return (
      <div className="flex h-screen bg-surface items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-card bg-gradient-to-br from-primary-500 to-purple-600 flex items-center justify-center">
            <span className="text-white font-extrabold text-sm">W</span>
          </div>
          <div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" />
        </div>
      </div>
    );
  }

  // Work mode detection: work.weship.eu hostname or sessionStorage flag
  const isWorkHost = isWorkHostGlobal;
  const isWorkMode = isWorkModeGlobal;

  // Not logged in -> show login (or WorkLogin for work mode)
  if (!user) {
    if (isWorkMode) return <WorkLogin />;
    return <Login />;
  }

  // MCP-OAuth Consent (#1643): auth-gated standalone Seite (kein App-Chrome).
  // Nach Login landet der User über die erhaltene URL wieder hier.
  if (isOAuthConsent) {
    return (
      <ErrorBoundary resetKeys={[location.pathname]} label="oauth-consent">
        <OAuthConsent />
      </ErrorBoundary>
    );
  }

  // Kunden-Portal: eigenes Layout mit PortalSidebar (not in work mode)
  if (!isWorkMode && !user.work_mode && ['kunde', 'portal_kunde'].includes(user.rolle)) {
    return <KundenPortalLayout collapsed={sidebarMode !== 'pinned'} setCollapsed={(v) => setSidebarMode(v ? 'collapsed' : 'pinned')} />;
  }

  // Work mode or Portal-Mitarbeiter: Mobile-First Layout mit Bottom-Navigation
  if (isWorkMode || user.work_mode || user.rolle === 'portal_mitarbeiter') {
    return (
      <PortalMobileLayout>
        <ErrorBoundary resetKeys={[location.pathname]} label="portal-mitarbeiter">
          <Routes>
            <Route path="/portal" element={<MobileHome />} />
            <Route path="/portal/zeiten" element={<PortalZeitkontoPage />} />
            <Route path="/portal/abwesend" element={<MobileAbwesend />} />
            <Route path="/portal/dokumente" element={<MobileDokumente />} />
            <Route path="/portal/profil" element={<MobileProfil />} />
            <Route path="/portal/notifications" element={<MobileNotifications />} />
            <Route path="/portal/news" element={<MaPortalNews />} />
            <Route path="/portal/news/:id" element={<MaPortalNews />} />
            <Route path="/portal/abwesenheiten" element={<PortalAbwesendRedirect />} />
            <Route path="/portal/zeitkonto" element={<Navigate to="/portal/zeiten" replace />} />
            <Route path="/portal/vertraege" element={<PortalVertraege />} />
            <Route path="/portal/schulungen" element={<PortalSchulungen />} />
            <Route path="/portal/schulungen/:zuweisungId" element={<PortalSchulungDetail />} />
            <Route path="/portal/vorschlaege" element={<PortalVorschlaege />} />
            <Route path="/portal/urlaub" element={<PortalAbwesendRedirect />} />
            <Route path="/portal/krankmeldung" element={<Navigate to="/portal/abwesend?typ=krank" replace />} />
            <Route path="/portal/lohnzettel" element={<Navigate to="/portal/dokumente" replace />} />
            <Route path="*" element={<Navigate to="/portal" replace />} />
          </Routes>
        </ErrorBoundary>
      </PortalMobileLayout>
    );
  }

  // JV-Partner: minimal layout with Sidebar + TopBar, only FinecomPage route
  if (user.rolle === 'jv_partner') {
    return (
      <>
        <div className="flex h-screen bg-surface text-content overflow-hidden">
          <Sidebar sidebarMode={sidebarMode} setSidebarMode={setSidebarMode} />
          <div className="flex-1 flex flex-col overflow-hidden">
            <TopBar isDev={isDev} />
            <main className="flex-1 flex flex-col overflow-y-auto bg-surface">
              <ErrorBoundary resetKeys={[location.pathname]} label="jv-partner">
                <Routes>
                  <Route path="/finecom" element={<FinecomPage />} />
                  <Route path="/profil" element={<Profile />} />
                  <Route path="*" element={<Navigate to="/finecom" replace />} />
                </Routes>
              </ErrorBoundary>
            </main>
          </div>
        </div>
      </>
    );
  }

  const isTV = location.pathname === '/kunden-rotation/tv';

  return (
    <>
      <div className="flex h-screen bg-surface text-content overflow-hidden">
        {!isTV && <Sidebar sidebarMode={sidebarMode} setSidebarMode={setSidebarMode} />}
        <div className={isTV ? 'w-full h-full overflow-hidden' : 'flex-1 flex flex-col overflow-hidden'}>
          {!isTV && <TopBar isDev={isDev} />}
          <main className={isTV ? 'w-full h-full overflow-hidden' : 'flex-1 flex flex-col overflow-y-auto bg-surface'}>
            <ErrorBoundary resetKeys={[location.pathname]} label="admin">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/dashboard" element={<Home />} />
              <Route path="/aufgaben" element={<Aufgaben />} />
              <Route path="/meine-aufgaben" element={<MeineAufgaben />} />
              <Route path="/kalender" element={<Kalender />} />
              <Route path="/kalender/buchen" element={<TerminBuchen />} />
              <Route path="/operations" element={<Operations />} />
              <Route path="/operations/lieferauftraege-pruefung" element={<OutboundInvestigations />} />
              <Route path="/packplatz-timer" element={<PackplatzTimer />} />
              <Route path="/forecast" element={<Forecast />} />
              <Route path="/forecast/quality" element={<ForecastQuality />} />
              <Route path="/forecast/bulk" element={<ForecastBulk />} />
              <Route path="/commitments" element={<Commitments />} />
              <Route path="/firmen-registrierungen" element={<CompanyRegistrations />} />
              <Route path="/commitments/zur-genehmigung" element={<CommitmentsZurGenehmigung />} />
              <Route path="/commitments/:id" element={<CommitmentDetail />} />
              <Route path="/forecast/aktionszeitraeume" element={<Navigate to="/forecast-review?seg=aktionszeitraeume" replace />} />
              <Route path="/forecast-overview" element={<ForecastOverview />} />
              <Route path="/forecast-review" element={<ForecastReview />} />
              <Route path="/workforce" element={<Workforce />} />
              <Route path="/schichtplanung" element={<Schichtplanung />} />
              <Route path="/schichtplanung/wochenplan" element={<WochenplanPage />} />
              <Route path="/schichtplanung/tageseinteilung" element={<Navigate to="/schichtplanung/wochenplan" replace />} />
              <Route path="/personalplanung" element={<Personalplanung />} />
              <Route path="/kunden-rotation" element={<Navigate to="/schichtplanung/wochenplan?tab=rotation" replace />} />
              <Route path="/kunden-rotation/tv" element={<RotationTV />} />
              <Route path="/performance" element={<Performance />} />
              <Route path="/feiertage" element={<Feiertage />} />
              <Route path="/mitarbeiter" element={<MitarbeiterPage />} />
              <Route path="/mitarbeiter/onboarding" element={<OnboardingPage />} />
              <Route path="/mitarbeiter/pulse" element={<MitarbeiterPulsePage />} />
              <Route path="/mitarbeiter/:id/pulse" element={<MitarbeiterPulseDetail />} />
              <Route path="/mitarbeiter/:id" element={<MitarbeiterDetail />} />
              <Route path="/kunden" element={<KundenPage />} />
              <Route path="/kontakte" element={<Contacts />} />
              <Route path="/kunden/adressklaerungen" element={<AdressklaerungPage />} />
              <Route path="/kunden/adressklaerungen/stats" element={<AdressklaerungStats />} />
              <Route path="/kunden/:id" element={<KundenDetail />} />
              <Route path="/cases" element={<Navigate to="/cases/liste" replace />} />
              <Route path="/cases/board" element={<Navigate to="/cases/liste" replace />} />
              <Route path="/cases/liste" element={<Cases />} />
              <Route path="/cases/:id" element={<Cases />} />
              <Route path="/cases/reports" element={<CasesReports />} />
              <Route path="/cases/flowai-report" element={<CasesFlowaiReport />} />
              <Route path="/cases/einstellungen" element={<CasesEinstellungen />} />
              <Route path="/cases/konfiguration" element={<CasesKonfiguration />} />
              <Route path="/claims" element={<ClaimsDashboard />} />
              <Route path="/claims/new" element={<ClaimsFormular />} />
              <Route path="/claims/knowledge" element={<ClaimsKnowledge />} />
              <Route path="/claims/inbox" element={<UnassignedInbox />} />
              <Route path="/claims/settings" element={<ClaimsSettings />} />
              <Route path="/claims/:id" element={<ClaimDetail />} />
              <Route path="/flowai" element={<FlowAICockpit />} />
              <Route path="/flowai/uebersicht" element={<FlowAIUebersicht />} />
              <Route path="/flowai/workspaces" element={<FlowAIWorkspaceList />} />
              <Route path="/flowai/workspaces/:id" element={<FlowAIWorkspaceDetail />} />
              <Route path="/flowai/playbooks" element={<FlowAIPlaybookList />} />
              <Route path="/flowai/playbooks/new" element={<FlowAIPlaybookEditor />} />
              <Route path="/flowai/playbooks/:id" element={<FlowAIPlaybookEditor />} />
              <Route path="/flowai/playbooks/:id/builder" element={<FlowAIPlaybookBuilder />} />
              <Route path="/flowai/skills" element={<FlowAISkillList />} />
              <Route path="/flowai/skills/new" element={<FlowAISkillEditor />} />
              <Route path="/flowai/skills/:id" element={<FlowAISkillEditor />} />
              <Route path="/flowai/approvals" element={<FlowAIApprovalInbox />} />
              <Route path="/flowai/audit" element={<FlowAIAudit />} />
              <Route path="/flowai/memory" element={<FlowAIMemory />} />
              <Route path="/flowai/events" element={<FlowAIEvents />} />
              <Route path="/flowai/admin/email" element={<FlowAIEmailConfig />} />
              <Route path="/flowai/admin/safety" element={<FlowAISafety />} />
              <Route path="/flowai/voice" element={<FlowAIVoice />} />
              <Route path="/softphone-test" element={<SoftphoneTest />} />
              <Route path="/flowai/chat" element={<FlowAIChat />} />
              <Route path="/flowai/chat/:sessionId" element={<FlowAIChat />} />
              <Route path="/chat" element={<FlowAIChat />} />
              <Route path="/chat/:sessionId" element={<FlowAIChat />} />
              <Route path="/standorte" element={<StandortePage />} />
              <Route path="/rohdaten" element={<Rohdaten />} />
              <Route path="/kostenstellen" element={<Segmentanalyse />} />
              <Route path="/anwesenheiten" element={<Anwesenheiten />} />
              <Route path="/anwesenheiten/ausnahmen" element={<AusnahmenPage />} />
              <Route path="/abwesenheiten/kalender" element={<UrlaubKalenderPage />} />
              <Route path="/abwesenheiten/antraege" element={<UrlaubAntraegePage />} />
              <Route path="/abwesenheiten/uebersicht" element={<UrlaubUebersichtPage />} />
              <Route path="/abwesenheiten/liste" element={<AbwesenheitenListePage />} />
              <Route path="/urlaub/*" element={<Navigate to="/abwesenheiten/kalender" replace />} />
              <Route path="/zeitkonto" element={<ZeitkontoPage />} />
              <Route path="/zeiterfassung" element={<Zeiterfassung />} />
              <Route path="/lohnverrechnung" element={<LohnverrechnungPage />} />
              <Route path="/lohnverrechnung/import" element={<LohnverrechnungPage />} />
              <Route path="/lohnverrechnung/monats-bestaetigung" element={<LohnverrechnungPage />} />
              <Route path="/lohnverrechnung/lv-report" element={<LohnverrechnungPage />} />
              <Route path="/lohnverrechnung/einstellungen" element={<LohnverrechnungPage />} />
              <Route path="/carrier-kontrolle" element={<CarrierKontrolle />} />
              <Route path="/zufriedenheit" element={<Zufriedenheit />} />
              <Route path="/deckungsbeitrag" element={<Deckungsbeitrag />} />
              <Route path="/jv-deckungsbeitrag" element={<JvDeckungsbeitragPage />} />
              <Route path="/zeitvorgaben" element={<Navigate to="/zeitvorgaben-analyse" replace />} />
              <Route path="/zeitvorgaben-analyse" element={<ZeitvorgabenAnalyse />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/idle-time" element={<IdleTime />} />
              <Route path="/finecom" element={<FinecomPage />} />
              <Route path="/controlling/kpi" element={<ControllingKpiPage />} />
              <Route path="/controlling/zeit-kosten" element={<ControllingZeitKosten />} />
              <Route path="/personalkosten" element={<PersonalkostenPage />} />
              <Route path="/sales/dashboard" element={<SalesDashboard />} />
              <Route path="/sales/funnels" element={<SalesFunnels />} />
              <Route path="/sales/anfragen" element={<SalesAnfragen />} />
              <Route path="/sales/onboarding" element={<SalesOnboarding />} />
              <Route path="/sales/onboarding/meine-aufgaben" element={<Navigate to="/meine-aufgaben" replace />} />
              <Route path="/sales/einstellungen" element={<SalesEinstellungen />} />
              <Route path="/sales/workflows" element={<SalesWorkflows />} />
              <Route path="/sales/workflows/:id" element={<WorkflowEditor />} />
              <Route path="/sales/lead-finder" element={<LeadFinder />} />
              <Route path="/sales/leads/:id" element={<LeadDetail />} />
              <Route path="/preiskalkulator" element={<PreiskalkulatorPage />} />
              <Route path="/preiskalkulator/config" element={<PreiskalkulatorConfig />} />
              <Route path="/preiskalkulator/routen" element={<PreiskalkulatorRouten />} />
              <Route path="/preiskalkulator/anfragen" element={<PreiskalkulatorAnfragen />} />
              {/* Recruiting — unified Fest + Flex */}
              <Route path="/recruiting" element={<RecruitingDashboardUnified />} />
              <Route path="/recruiting/bewerber" element={<RecruitingBewerberUnified />} />
              <Route path="/recruiting/bewerber/:id" element={<RecruitingDetailUnified />} />
              <Route path="/recruiting/stellen" element={<RecruitingStellen />} />
              <Route path="/recruiting/einsaetze" element={<FlexEinsaetzePage />} />
              <Route path="/recruiting/einsaetze/:id" element={<FlexEinsatzDetail />} />
              <Route path="/recruiting/pool" element={<FlexPoolPage />} />
              <Route path="/recruiting/reports" element={<RecruitingReportsUnified />} />
              <Route path="/recruiting/karriere" element={<KarrierePage />} />
              <Route path="/recruiting/einstellungen" element={<RecruitingEinstellungenUnified />} />
              <Route path="/vertraege" element={<VertraegePage />} />
              {/* Legacy Flex redirects → unified Recruiting */}
              <Route path="/flex" element={<Navigate to="/recruiting" replace />} />
              <Route path="/flex/bewerber" element={<Navigate to="/recruiting/bewerber?typ=flex" replace />} />
              <Route path="/flex/bewerber/:id" element={<FlexBewerberRedirect />} />
              <Route path="/flex/einsaetze" element={<Navigate to="/recruiting/einsaetze" replace />} />
              <Route path="/flex/einsaetze/:id" element={<FlexEinsatzRedirect />} />
              <Route path="/flex/pool" element={<Navigate to="/recruiting/pool" replace />} />
              <Route path="/flex/reports" element={<Navigate to="/recruiting/reports" replace />} />
              <Route path="/flex/einstellungen" element={<Navigate to="/recruiting/einstellungen" replace />} />
              <Route path="/aufnahmen" element={<AufnahmenPage />} />
              <Route path="/telefonie" element={<TelefonieDashboardPage />} />
              <Route path="/telefonie/verlauf" element={<TelefonieVerlaufPage />} />
              <Route path="/telefonie/aufnahmen" element={<TelefonieAufnahmenPage />} />
              <Route path="/telefonie/regeln" element={<TelefonieRegelnPage />} />
              <Route path="/telefonie/einstellungen" element={<TelefonieEinstellungenPage />} />
              <Route path="/prozesse" element={<Navigate to="/prozesse/abteilungen" replace />} />
              <Route path="/prozesse/abteilungen" element={<ProzesseAbteilungenPage />} />
              <Route path="/prozesse/abteilungen/:slug" element={<ProzessAbteilungDetailPage />} />
              <Route path="/prozesse/telegram" element={<ProzessTelegramPage />} />
              <Route path="/qm/feedback" element={<QmFeedbackPage />} />
              <Route path="/qm/pipeline" element={<QmPipeline />} />
              <Route path="/prozesse/:id" element={<ProzessDetailPage />} />
              <Route path="/ausweise" element={<AusweisePage />} />
              <Route path="/organigramm" element={<OrganigrammPage />} />
              <Route path="/hr-audit" element={<HrAudit />} />
              <Route path="/hr/austritte" element={<HrAustritte />} />
              <Route path="/kunden-forecasts" element={<Navigate to="/forecast-review" replace />} />
              <Route path="/kunden-forecasts/:id" element={<CustomerForecastDetail />} />
              <Route path="/inbound-management" element={<InboundManagement />} />
              <Route path="/warehouse/lagerplaetze" element={<WarehouseLagerplaetze />} />
              <Route path="/warehouse/simulation" element={<WarehouseSimulation />} />
              <Route path="/warehouse/einrichten" element={<WarehouseConfig />} />
              <Route path="/warehouse" element={<Navigate to="/warehouse/lagerplaetze" replace />} />
              <Route path="/warehouse/stock" element={<Navigate to="/warehouse/lagerplaetze" replace />} />
              <Route path="/warehouse/config" element={<Navigate to="/warehouse/einrichten" replace />} />
              <Route path="/data-hub" element={<ApiConfig />} />
              <Route path="/api-config" element={<Navigate to="/data-hub" replace />} />
              <Route path="/profil" element={<Profile />} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/bug-reports/:id/verify" element={<BugReportVerify />} />
              <Route path="/admin/bug-reports" element={<BugReportInbox />} />
              <Route path="/admin/bug-reports/:id" element={<BugReportDetail />} />
              <Route path="/admin" element={<AdminHubPage />} />
              <Route path="/admin/users" element={<UserAdmin />} />
              <Route path="/admin/segmente" element={<SegmentConfig />} />
              <Route path="/admin/email" element={<EmailVerwaltung />} />
              <Route path="/admin/sms-templates" element={<SmsTemplates />} />
              <Route path="/admin/notification-events" element={<NotificationEventsAdmin />} />
              <Route path="/admin/rollen" element={<RolePermissionsAdmin />} />
              <Route path="/admin/szenarien" element={<ScenariosAdmin />} />
              <Route path="/admin/notification-log" element={<NotificationLogAdmin />} />
              <Route path="/admin/chat" element={<ChatAdmin />} />
              <Route path="/admin/integrationen" element={<IntegrationenAdmin />} />
              <Route path="/admin/freshdesk-analyse" element={<FreshdeskAnalysePage />} />
              <Route path="/einstellungen" element={<Einstellungen />} />
              {/* Handbuch & Changelog */}
              <Route path="/handbuch" element={<Handbuch />} />
              <Route path="/handbuch/:slug" element={<Handbuch />} />
              <Route path="/dev-handbuch" element={<DevHandbuch />} />
              <Route path="/dev-handbuch/:slug" element={<DevHandbuch />} />
              <Route path="/changelog" element={<Changelog />} />
              <Route path="/admin/deployment" element={<DeploymentPage />} />
              <Route path="/entwicklung/system-pulse" element={<SystemPulse />} />
              {/* Mitarbeiter-Portal Routes */}
              <Route path="/portal" element={<MaPortal />} />
              <Route path="/portal/news" element={<MaPortalNews />} />
              <Route path="/portal/news/:id" element={<MaPortalNews />} />
              <Route path="/portal/profil" element={<MaPortalProfil />} />
              <Route path="/portal/dokumente" element={<MaPortalDokumente />} />
              <Route path="/portal/zeiten" element={<MaPortalZeiten />} />
              <Route path="/portal/abwesenheiten" element={<PortalAbwesenheiten />} />
              <Route path="/portal/zeitkonto" element={<Navigate to="/portal/zeiten" replace />} />
              <Route path="/portal/vertraege" element={<Navigate to="/portal/dokumente" replace />} />
              <Route path="/portal/schulungen" element={<PortalSchulungen />} />
              <Route path="/portal/schulungen/:zuweisungId" element={<PortalSchulungDetail />} />
              <Route path="/portal/vorschlaege" element={<PortalVorschlaege />} />
              {/* Redirects für alte Routen */}
              <Route path="/portal/urlaub" element={<Navigate to="/portal/abwesenheiten" replace />} />
              <Route path="/portal/krankmeldung" element={<Navigate to="/portal/abwesenheiten" replace />} />
              <Route path="/portal/lohnzettel" element={<Navigate to="/portal/dokumente" replace />} />
              <Route path="/login" element={<Navigate to="/" replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </ErrorBoundary>
          </main>
        </div>
      </div>
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppModeProvider>
        <AppContent />
        {/* CopilotChat (#1002) — global deaktiviert */}
        <TimerFloatingPanel />
      </AppModeProvider>
    </AuthProvider>
  );
}
